/**
 * A compact markdown writing field for notes.
 *
 * Same dual-layer highlighter as the document editor (`MarkdownEditor`), in
 * `variant="compact"` so it fits modals and sidebars. The value is still
 * markdown source — Preview toggles to rendered HTML so you can check how a
 * note will read before saving.
 *
 * Public API stays controlled (`value` / `onValueChange`) for the notes call
 * sites; the underlying editor is uncontrolled and synced via its handle.
 */

import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Eye, Pencil } from "lucide-react";
import {
  MarkdownEditor,
  type MarkdownEditorHandle,
} from "@/components/editor/MarkdownEditor";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/ui/markdown";
import { cn } from "@/lib/utils";

interface MarkdownFieldProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Height of the preview, so toggling does not resize the surrounding card. */
  previewClassName?: string;
  id?: string;
  "aria-label"?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

export function MarkdownField({
  value,
  onValueChange,
  placeholder,
  className,
  previewClassName,
  id,
  "aria-label": ariaLabel,
  onKeyDown,
}: MarkdownFieldProps) {
  const [showPreview, setShowPreview] = useState(false);
  const editorRef = useRef<MarkdownEditorHandle>(null);
  const hasContent = value.trim().length > 0;

  // Keep the uncontrolled editor in step with the controlled value (load,
  // cancel-edit resets, remount after Preview).
  useLayoutEffect(() => {
    if (showPreview) return;
    const editor = editorRef.current;
    if (!editor) return;
    if (editor.getMarkdown() !== value) editor.setMarkdown(value);
  }, [value, showPreview]);

  const handleChange = useCallback(
    (next: string) => {
      onValueChange(next);
    },
    [onValueChange],
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">Markdown supported</span>
        {hasContent && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setShowPreview((on) => !on)}
          >
            {showPreview ? (
              <>
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Write
              </>
            ) : (
              <>
                <Eye className="h-3.5 w-3.5 mr-1.5" />
                Preview
              </>
            )}
          </Button>
        )}
      </div>

      {showPreview && hasContent ? (
        <Markdown
          content={value}
          className={cn(
            "rounded-md border border-input bg-muted/30 px-3 py-2 min-h-[80px]",
            previewClassName,
          )}
        />
      ) : (
        <MarkdownEditor
          ref={editorRef}
          id={id}
          variant="compact"
          className={cn("max-h-64", className)}
          placeholder={placeholder}
          ariaLabel={ariaLabel ?? placeholder ?? "Note"}
          onChange={handleChange}
          onKeyDown={onKeyDown}
        />
      )}
    </div>
  );
}
